/**
 * Your Build Widget Default Configuration
 *
 * Displays the user's current build/configuration summary.
 * Non-reusable widget - only one per page.
 */

export interface ButtonConfig {
  showText: boolean;
  buttonLabel: string;
  showIcon: boolean;
  iconUrl?: string;
}

export interface AddToCartButtonConfig extends ButtonConfig {
  saveToPermanentStorage: boolean;
  addingToCartMessage: string;
}

export interface YourBuildWidgetConfig {
  id: string;
  miniWidgetShowText: boolean;
  miniWidgetText: string;
  miniWidgetShowPrice: boolean;
  miniWidgetShowItemCount: boolean;
  miniWidgetOnClick: string;
  showTitle: boolean;
  title: string;
  showShareButton: boolean;
  shareButtonConfig: ButtonConfig;
  showDescription: boolean;
  showDescriptionTitle: boolean;
  descriptionTitle: string;
  descriptionTitleCollapseExpand: boolean;
  description: string;
  showAddToCartButton: boolean;
  addToCartButtonConfig: AddToCartButtonConfig;
}

export const YOUR_BUILD_DEFAULT_CONFIG: YourBuildWidgetConfig = {
  id: '',
  miniWidgetShowText: true,
  miniWidgetText: 'Your Build',
  miniWidgetShowPrice: true,
  miniWidgetShowItemCount: true,
  miniWidgetOnClick: '',
  showTitle: true,
  title: 'Your Build',
  showShareButton: false,
  shareButtonConfig: {
    showText: true,
    buttonLabel: 'Share',
    showIcon: false,
  },
  showDescription: true,
  showDescriptionTitle: true,
  descriptionTitle: '',
  descriptionTitleCollapseExpand: true,
  description: 'Disclaimer: Compatio provides product configurations and recommendations as a guide.',
  showAddToCartButton: true,
  addToCartButtonConfig: {
    showText: true,
    buttonLabel: 'Add to Cart',
    showIcon: false,
    saveToPermanentStorage: false,
    addingToCartMessage: 'Adding selected product(s) to the cart',
  },
};

/**
 * Create default configuration for a Your Build widget
 */
export const createYourBuildConfig = (): YourBuildWidgetConfig => ({
  ...YOUR_BUILD_DEFAULT_CONFIG,
  id: `your-build-widget-instance-${crypto.randomUUID()}`,
});

type YourBuildConfigInput = Omit<Partial<YourBuildWidgetConfig>, 'shareButtonConfig' | 'addToCartButtonConfig'> & {
  shareButtonConfig?: Partial<ButtonConfig>;
  addToCartButtonConfig?: Partial<AddToCartButtonConfig>;
};

/**
 * Merge config with defaults to ensure all fields exist
 * Useful for backward compatibility when new fields are added
 */
export const mergeWithDefaults = (
  config: YourBuildConfigInput
): YourBuildWidgetConfig => ({
  ...YOUR_BUILD_DEFAULT_CONFIG,
  ...config,
  shareButtonConfig: {
    ...YOUR_BUILD_DEFAULT_CONFIG.shareButtonConfig,
    ...config.shareButtonConfig,
  },
  addToCartButtonConfig: {
    ...YOUR_BUILD_DEFAULT_CONFIG.addToCartButtonConfig,
    ...config.addToCartButtonConfig,
  },
});

export default createYourBuildConfig;
